import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Lead, LeverageClient, LeverageContribution, LeverageOperation, LeverageOperationInput, LeverageOperationStatus, Page,
  BrokerSymbol, SignalSettings, SignalSettingsInput,
} from '@/lib/types';

/**
 * Leituras e escritas da alavancagem. Ficam fora do queries.ts porque só esta
 * página as usa; a invalidação cobre o que a alavancagem muda: ela mesma e a
 * conta do cliente, que o aporte pode completar.
 */

const leverageKey = (part: string, params?: unknown) => ['leverage', part, params] as const;

function useInvalidateLeverage() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['leverage'] });
    void queryClient.invalidateQueries({ queryKey: ['leads'] });
  };
}

/** Os clientes com aporte e o capital de cada um. */
export const useLeverageClients = () =>
  useQuery({
    queryKey: leverageKey('clients'),
    queryFn: () => api.get<{ items: LeverageClient[] }>('/api/leverage/clients'),
  });

export const useLeverageContributions = (params: { page: number; pageSize?: number; leadId?: string }) =>
  useQuery({
    queryKey: leverageKey('contributions', params),
    queryFn: () => api.get<Page<LeverageContribution>>('/api/leverage/contributions', params),
    placeholderData: (previous) => previous,
  });

export const useLeverageOperations = (params: { status?: LeverageOperationStatus }, options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: leverageKey('operations', params),
    queryFn: () => api.get<{ items: LeverageOperation[] }>('/api/leverage/operations', params),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });

/** O cliente escolhido no aporte: diz se a conta na corretora já está completa. */
export const useLeadAccount = (leadId: string) =>
  useQuery({
    queryKey: ['leads', 'detail', leadId],
    queryFn: () => api.get<{ lead: Lead }>(`/api/leads/${leadId}`),
    enabled: Boolean(leadId),
    select: (data) => ({ accountLogin: data.lead.accountLogin, hasAccountPassword: data.lead.hasAccountPassword }),
  });

export function useUpdateContribution() {
  const invalidate = useInvalidateLeverage();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; amount?: number; date?: string; time?: string; note?: string | null }) =>
      api.patch<{ contribution: LeverageContribution }>(`/api/leverage/contributions/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteContribution() {
  const invalidate = useInvalidateLeverage();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/leverage/contributions/${id}`),
    onSuccess: invalidate,
  });
}

export function useSaveOperation() {
  const invalidate = useInvalidateLeverage();
  return useMutation({
    mutationFn: ({ id, ...body }: LeverageOperationInput & { id?: string }) =>
      id
        ? api.patch<{ operation: LeverageOperation }>(`/api/leverage/operations/${id}`, body)
        : api.post<{ operation: LeverageOperation }>('/api/leverage/operations', body),
    onSuccess: invalidate,
  });
}

export function useSetOperationState() {
  const invalidate = useInvalidateLeverage();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'close' | 'reopen' }) =>
      api.post<{ operation: LeverageOperation }>(`/api/leverage/operations/${id}/${action}`),
    onSuccess: invalidate,
  });
}

export function useDeleteOperation() {
  const invalidate = useInvalidateLeverage();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/leverage/operations/${id}`),
    onSuccess: invalidate,
  });
}

export function useSetExecuted() {
  const invalidate = useInvalidateLeverage();
  return useMutation({
    mutationFn: ({ operationId, allocationId, executed }: { operationId: string; allocationId: string; executed: boolean }) => {
      const path = `/api/leverage/operations/${operationId}/allocations/${allocationId}/executed`;
      return executed ? api.post<{ operation: LeverageOperation }>(path) : api.delete<{ operation: LeverageOperation }>(path);
    },
    onSuccess: invalidate,
  });
}

/** A configuração dos sinais da corretora (só o administrador). */
export const useSignalSettings = (options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: leverageKey('signal-settings'),
    queryFn: () => api.get<{ settings: SignalSettings }>('/api/leverage/signal-settings'),
    select: (data) => data.settings,
    enabled: options.enabled ?? true,
  });

export function useSaveSignalSettings() {
  const invalidate = useInvalidateLeverage();
  return useMutation({
    mutationFn: (body: SignalSettingsInput) => api.put<{ settings: SignalSettings }>('/api/leverage/signal-settings', body),
    onSuccess: invalidate,
  });
}

/** Manda o sinal da operação para a corretora. Os avisos novos aparecem no sino. */
export function useSendSignal() {
  const invalidate = useInvalidateLeverage();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, again }: { id: string; again?: boolean }) =>
      api.post<{ operation: LeverageOperation }>(`/api/leverage/operations/${id}/signal`, { again: Boolean(again) }),
    onSettled: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Os ativos da corretora para o slot configurado (o servidor guarda 5 min). */
export const useBrokerSymbols = (options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: leverageKey('symbols'),
    queryFn: () => api.get<{ items: BrokerSymbol[]; slot: string | null; fetchedAt: string | null }>('/api/leverage/symbols'),
    staleTime: 5 * 60_000,
    retry: 1,
    enabled: options.enabled ?? true,
  });
